import { supabase } from "../SupabaseClient.tsx";

// Remembers how someone first found Shoebox, so a new account can record which channel brought it.
// The marketing site sets the same cookie, which is how the answer survives the hop to the app.
// Only the referring site, landing page, and campaign tags are kept, never a full URL.

const COOKIE_NAME = "sb_first_touch";
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const NEW_ACCOUNT_MS = 48 * 60 * 60 * 1000;
const MAX_VALUE_LENGTH = 80;
const CAMPAIGN_TAGS = ["utm_source", "utm_medium", "utm_campaign"] as const;

export type FirstTouch = Partial<
  Record<"referrer" | "landing" | "ad_click" | "at" | (typeof CAMPAIGN_TAGS)[number], string>
>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function referringSite(referrer: string): string | null {
  try {
    const host = new URL(referrer).hostname;
    return /(^|\.)shoebox\.io$/.test(host) ? null : host;
  } catch {
    return null;
  }
}

export function describeVisit(url: URL, referrer: string, now: Date): FirstTouch {
  const visit: FirstTouch = {
    landing: url.pathname.slice(0, MAX_VALUE_LENGTH),
    at: now.toISOString().slice(0, 10),
  };

  const site = referringSite(referrer);
  if (site) visit.referrer = site.slice(0, MAX_VALUE_LENGTH);

  for (const tag of CAMPAIGN_TAGS) {
    const value = url.searchParams.get(tag);
    if (value) visit[tag] = value.slice(0, MAX_VALUE_LENGTH);
  }
  if (url.searchParams.has("gclid")) visit.ad_click = "google";

  return visit;
}

export function readFirstTouch(): FirstTouch | null {
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${COOKIE_NAME}=`));
  if (!cookie) return null;

  try {
    const parsed = JSON.parse(decodeURIComponent(cookie.slice(COOKIE_NAME.length + 1)));
    const firstTouch: FirstTouch = {};
    for (const key of ["referrer", "landing", "ad_click", "at", ...CAMPAIGN_TAGS] as const) {
      if (typeof parsed[key] === "string") firstTouch[key] = parsed[key].slice(0, MAX_VALUE_LENGTH);
    }
    return firstTouch;
  } catch {
    return null;
  }
}

// The first visit wins, so a later visit from a bookmark doesn't hide what brought someone here
export function rememberFirstTouch() {
  if (readFirstTouch()) return;

  const visit = describeVisit(new URL(window.location.href), document.referrer, new Date());
  const domain = /(^|\.)shoebox\.io$/.test(window.location.hostname) ? ";domain=.shoebox.io" : "";
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(visit))}` +
    `;path=/;max-age=${COOKIE_MAX_AGE_SECONDS};samesite=lax;secure${domain}`;
}

// Saves the channel once for a new account, and tells analytics about the signup at the same moment
export async function recordNewSignup(user: { id: string; created_at?: string }) {
  const age = Date.now() - new Date(user.created_at ?? NaN).getTime();
  if (!(age < NEW_ACCOUNT_MS)) return;

  const { data, error } = await supabase
    .from("onboarding")
    .update({ first_touch: readFirstTouch() ?? {} })
    .eq("user_id", user.id)
    .is("first_touch", null)
    .select("user_id");

  if (error) {
    console.error("Error saving how this account found us:", error.message);
    return;
  }
  if (data?.length) window.gtag?.("event", "sign_up");
}
